import { useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import styled from 'styled-components'
import { TextField, MenuItem, CircularProgress } from '@mui/material'
import AttachFileIcon from '@mui/icons-material/AttachFile'
import CloseIcon from '@mui/icons-material/Close'
import {
  generateSuggestionsRoutine,
  selectSuggestionsLoading,
} from 'features/incidents/ducks'
import IncidentsAPI from 'services/IncidentsAPI'

const Wrap = styled.div`
  background: white;
  border: 1px solid ${({ theme }) => theme.colors.lightGrey};
  border-radius: 12px;
  padding: 24px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.04);
`
const Field = styled.div`
  margin-bottom: 16px;
`
const Footer = styled.div`
  display: flex;
  justify-content: flex-end;
  margin-top: 8px;
`
const SubmitButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 10px 18px;
  background: ${({ theme }) => theme.colors.haiti};
  color: white;
  border: none;
  border-radius: 8px;
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
  &:hover { background: ${({ theme }) => theme.colors.primary}; }
  &:disabled { opacity: 0.5; cursor: not-allowed; }
`
const AttachRow = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
  margin-bottom: 16px;
  padding: 12px 14px;
  border: 1px dashed ${({ theme }) => theme.colors.lightGrey};
  border-radius: 12px;
  background: #fafafa;
`
const AttachTopRow = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
`
const AttachButton = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 6px 12px;
  background: white;
  color: ${({ theme }) => theme.colors.haiti};
  border: 1px solid ${({ theme }) => theme.colors.lightGrey};
  border-radius: 8px;
  font-size: 12px;
  font-weight: 500;
  cursor: ${({ $disabled }) => ($disabled ? 'not-allowed' : 'pointer')};
  opacity: ${({ $disabled }) => ($disabled ? 0.5 : 1)};
  transition: all 0.15s;
  &:hover {
    background: ${({ $disabled, theme }) => ($disabled ? 'white' : theme.colors.lightGrey)};
  }
`
const HintText = styled.span`
  font-size: 12px;
  color: #888;
`
const ChipRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
`
const FileChip = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 4px 8px 4px 10px;
  border-radius: 12px;
  font-size: 12px;
  max-width: 280px;
  background: ${({ $status }) =>
    $status === 'ready' ? '#ecfdf5' :
    $status === 'failed' ? '#fef2f2' :
    '#f1f5f9'};
  color: ${({ $status }) =>
    $status === 'ready' ? '#15803d' :
    $status === 'failed' ? '#b00020' :
    '#475569'};
  border: 1px solid ${({ $status }) =>
    $status === 'ready' ? '#bbf7d0' :
    $status === 'failed' ? '#fecaca' :
    '#e2e8f0'};
`
const FileChipName = styled.span`
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  max-width: 200px;
  font-weight: 500;
`
const FileChipRemove = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background: transparent;
  border: none;
  cursor: pointer;
  padding: 2px;
  border-radius: 999px;
  color: inherit;
  opacity: 0.7;
  &:hover { opacity: 1; background: rgba(0, 0, 0, 0.06); }
`

const IncidentForm = ({ availableBPs = [], onSubmitted, defaultBpId }) => {
  const dispatch = useDispatch()
  const loading = useSelector(selectSuggestionsLoading)

  const [bpId, setBpId] = useState(defaultBpId || availableBPs[0]?.id || '')
  const [narrative, setNarrative] = useState('')
  // attachments: [{ tempId, attachmentId, originalFilename, status: 'uploading'|'ready'|'failed' }]
  const [attachments, setAttachments] = useState([])

  useEffect(() => {
    if (defaultBpId) setBpId(defaultBpId)
  }, [defaultBpId])

  const handlePickFiles = async (event) => {
    const files = Array.from(event.target.files || [])
    event.target.value = ''
    if (!files.length || !bpId) return
    for (const file of files) {
      const tempId = `tmp-${Math.random().toString(36).slice(2, 10)}`
      setAttachments(a => [...a, {
        tempId,
        originalFilename: file.name,
        status: 'uploading',
      }])
      try {
        const presignRes = await IncidentsAPI.presignAttachment(bpId, {
          originalFilename: file.name,
          mimeType: file.type || 'application/octet-stream',
          sizeBytes: file.size,
        })
        const data = presignRes?.data?.data
        if (!data?.presignedUrl || !data?.attachmentId) {
          throw new Error('No presigned URL returned')
        }
        const putRes = await fetch(data.presignedUrl, {
          method: 'PUT',
          body: file,
          headers: { 'Content-Type': file.type || 'application/octet-stream' },
        })
        if (!putRes.ok) throw new Error(`S3 upload returned ${putRes.status}`)
        setAttachments(a => a.map(att =>
          att.tempId === tempId
            ? { ...att, attachmentId: data.attachmentId, status: 'ready' }
            : att
        ))
      } catch (e) {
        setAttachments(a => a.map(att =>
          att.tempId === tempId ? { ...att, status: 'failed' } : att
        ))
      }
    }
  }

  const removeAttachment = (tempId) => {
    setAttachments(a => a.filter(att => att.tempId !== tempId))
  }

  const submit = () => {
    const attachmentIds = attachments
      .filter(a => a.status === 'ready' && a.attachmentId)
      .map(a => a.attachmentId)
    dispatch(generateSuggestionsRoutine({
      buildingPassportId: bpId,
      narrative,
      attachmentIds,
    }))
    onSubmitted?.({ bpId, narrative, attachmentIds })
  }

  const showBpPicker = !defaultBpId
  const canSubmit = !loading && bpId && narrative.trim().length >= 10
    && !attachments.some(a => a.status === 'uploading')

  return (
    <Wrap>
      <h2 style={{ marginTop: 0, marginBottom: 16, fontSize: 18, fontWeight: 600 }}>Report an incident</h2>

      {showBpPicker && (
        <Field>
          <TextField
            select
            fullWidth
            required
            label="Building Passport"
            value={bpId}
            onChange={e => setBpId(e.target.value)}
            data-tour-id='incidentForm.bp-picker'
          >
            {availableBPs.map(bp => (
              <MenuItem key={bp.id} value={bp.id}>{bp.name}</MenuItem>
            ))}
          </TextField>
        </Field>
      )}

      <Field>
        <TextField
          multiline
          rows={6}
          fullWidth
          required
          label="What happened?"
          value={narrative}
          onChange={e => setNarrative(e.target.value)}
          helperText="Describe the incident — when, where, what you observed."
          data-tour-id='incidentForm.narrative'
        />
      </Field>

      <AttachRow>
        <AttachTopRow>
          <label>
            <input
              type="file"
              multiple
              hidden
              disabled={!bpId}
              onChange={handlePickFiles}
              accept="image/*,application/pdf"
            />
            <AttachButton $disabled={!bpId}>
              <AttachFileIcon style={{ fontSize: 16 }} /> Add attachment
            </AttachButton>
          </label>
          {!bpId && <HintText>Select a Building Passport first</HintText>}
        </AttachTopRow>
        {attachments.length > 0 && (
          <ChipRow>
            {attachments.map(att => (
              <FileChip key={att.tempId} $status={att.status}>
                {att.status === 'uploading' && (
                  <CircularProgress size={12} sx={{ color: 'inherit' }} />
                )}
                <FileChipName title={att.originalFilename}>{att.originalFilename}</FileChipName>
                {att.status === 'failed' && (
                  <span style={{ fontSize: 11 }}>upload failed</span>
                )}
                <FileChipRemove
                  type="button"
                  onClick={() => removeAttachment(att.tempId)}
                  aria-label="Remove attachment"
                >
                  <CloseIcon style={{ fontSize: 14 }} />
                </FileChipRemove>
              </FileChip>
            ))}
          </ChipRow>
        )}
      </AttachRow>

      <Footer>
        <SubmitButton type="button" onClick={submit} disabled={!canSubmit} data-tour-id='incidentForm.submit'>
          {loading ? 'Generating…' : 'Generate suggestions ✨'}
        </SubmitButton>
      </Footer>
    </Wrap>
  )
}

export default IncidentForm
